import type { StrapiId, User } from "@investhinks/api-client";

import Api from "../../../api";
import {
  ACTIVATE_EXTENDED_MODE,
  IMPERSONATE_USER,
  LOGIN,
  LOGOUT,
  REFRESH_IMPERSONATION,
  RETURN_TO_ADMIN,
} from "./actions";

export interface SessionState {
  token: string | null;
  user: User | null;
  extendedMode: boolean;
  adminToken: string | null;
  impersonatedUserId: StrapiId | null;
}

export interface RootState {
  sessionState: SessionState;
}

// Extended state with redux-persist metadata
export interface PersistedSessionState extends SessionState {
  _persist?: { rehydrated?: boolean };
}

export interface PersistedRootState {
  sessionState: PersistedSessionState;
}

interface SessionAction {
  type: string;
  payload?: {
    token?: string;
    user?: User | null;
    impersonationToken?: string;
    impersonatedUser?: User & { id: StrapiId };
    updatedUser?: User;
  };
}

const initialState: SessionState = {
  token: null,
  user: null,
  extendedMode: false,
  adminToken: null,
  impersonatedUserId: null,
};

// Legacy API params property - RestApiAdapter doesn't declare it but runtime uses it.
// We use Object.defineProperty to set it without needing type assertions.
function resetApiParams(): void {
  Object.defineProperty(Api, "params", { value: {}, writable: true, configurable: true });
}

const state = (state: SessionState = initialState, action: SessionAction): SessionState => {
  switch (action.type) {
    case LOGIN: {
      Api.token = action.payload?.token ?? null;
      resetApiParams();
      return {
        ...state,
        user: action.payload?.user ?? state.user,
        token: action.payload?.token ?? null,
      };
    }
    case LOGOUT: {
      Api.token = null;
      resetApiParams();
      return initialState;
    }
    case ACTIVATE_EXTENDED_MODE: {
      return {
        ...state,
        extendedMode: true,
      };
    }
    case IMPERSONATE_USER: {
      // Save current admin token before switching
      const impersonationToken = action.payload?.impersonationToken ?? null;
      Api.token = impersonationToken;
      return {
        ...state,
        adminToken: state.token,
        token: impersonationToken,
        user: action.payload?.impersonatedUser ?? state.user,
        impersonatedUserId: action.payload?.impersonatedUser?.id ?? null,
      };
    }
    case RETURN_TO_ADMIN: {
      // Restore admin token
      Api.token = state.adminToken;
      return {
        ...state,
        token: state.adminToken,
        adminToken: null,
        impersonatedUserId: null,
        // Note: user will be refreshed by calling refreshMe after this action
      };
    }
    case REFRESH_IMPERSONATION: {
      // Update impersonated user data without losing admin token
      return {
        ...state,
        user: action.payload?.updatedUser ?? state.user,
      };
    }
    default: {
      return state;
    }
  }
};

export default state;
